import type React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

import classes from './Modal.module.scss';
import type { ModalProps } from './types';
import { Button } from '../Button';
import { CloseIcon } from '../icons/Close.icon';
import { DislikeIcon } from '../icons/DislikeIcon';
import { LikeIcon } from '../icons/LikeIcon';
import { TextField } from '../TextField';

export const Modal: React.FC<ModalProps> = ({
  children,
  open,
  onClose,
  zIndex,
  widthModal,
  heightModal,
}) => {
  // Пока модалка открыта, страница под ней не прокручивается: подложка fixed закрывает
  // только экран, и при прокрутке была видна страница под ней. Счетчик нужен для
  // вложенных модалок.
  useEffect(() => {
    if (!open) return undefined;
    const body = document.body;
    const count = Number(body.dataset.modalLocks || 0);
    if (count === 0) {
      body.dataset.modalPrevOverflow = body.style.overflow;
      body.style.overflow = 'hidden';
    }
    body.dataset.modalLocks = String(count + 1);
    return () => {
      const left = Number(body.dataset.modalLocks || 1) - 1;
      body.dataset.modalLocks = String(left);
      if (left <= 0) {
        body.style.overflow = body.dataset.modalPrevOverflow || '';
        delete body.dataset.modalLocks;
        delete body.dataset.modalPrevOverflow;
      }
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return createPortal(
    <>
      <div
        style={{ zIndex }}
        className={clsx(classes.glassContainer, classes.backdrop, {
          [classes.open]: open,
        })}
        onClick={onClose}
      >
        <div className={classes.glassEffect}></div>
        <div className={classes.glassTint}></div>
        <div className={classes.glassShine}></div>
      </div>
      <div
        style={{
          zIndex: zIndex ? zIndex + 1 : undefined,
          width: widthModal ? widthModal : undefined,
          height: heightModal ? heightModal : undefined,
        }}
        className={clsx(classes.modal, { [classes.open]: open })}
        onClick={(event) => event.stopPropagation()}
        role='dialog'
        aria-modal='true'
      >
        <button type='button' className={classes.close} onClick={onClose} aria-label='Закрыть'>
          <CloseIcon />
        </button>
        {children}
      </div>
      <svg style={{ display: 'none' }}>
        <filter
          id='glass-distortion'
          x='0%'
          y='0%'
          width='100%'
          height='100%'
          filterUnits='objectBoundingBox'
        >
          <feTurbulence
            type='fractalNoise'
            baseFrequency='0.02 0.03'
            numOctaves='3'
            seed='42'
            result='turbulence'
          />
          <feComponentTransfer in='turbulence' result='mapped'>
            <feFuncR type='gamma' amplitude='1' exponent='1' offset='0.5' />
            <feFuncG type='gamma' amplitude='0' exponent='1' offset='0' />
            <feFuncB type='gamma' amplitude='0' exponent='1' offset='0.5' />
          </feComponentTransfer>

          <feGaussianBlur in='turbulence' stdDeviation='30' result='softMap' />

          <feSpecularLighting
            in='softMap'
            surfaceScale='5'
            specularConstant='1'
            specularExponent='100'
            lightingColor='white'
            result='specLight'
          >
            <fePointLight x='-200' y='-200' z='300' />
          </feSpecularLighting>

          <feComposite
            in='specLight'
            operator='arithmetic'
            k1='0'
            k2='1'
            k3='1'
            k4='0'
            result='litImage'
          />

          <feDisplacementMap
            in='SourceGraphic'
            in2='softMap'
            scale='150'
            xChannelSelector='R'
            yChannelSelector='G'
          />
        </filter>
      </svg>
    </>,
    document.body,
  );
};

export const FeedBackModalContent = () => {
  return (
    <form className={classes['feedback-form']}>
      <header>
        <h1>Оставьте отзыв</h1>
        <p>Как бы вы оценили взаимодействие с этим учеником?</p>
      </header>

      <div className={classes['feedback-qualiti']}>
        <Button
          startAdornment={<LikeIcon />}
          variant={'filled'}
          size={'md'}
          shape={'brick'}
          color={'success'}
        >
          <span>Хорошо</span>
        </Button>
        <Button
          startAdornment={<DislikeIcon />}
          variant={'filled'}
          size={'md'}
          shape={'brick'}
        >
          <span>Плохо</span>
        </Button>
      </div>

      <div className={classes['feedback-list']}>
        <Button variant={'filled'} size={'sm'} shape={'brick'}>
          Оплата вовремя
        </Button>
        <Button variant={'filled'} size={'sm'} shape={'brick'}>
          Пунктуальность
        </Button>
        <Button variant={'filled'} size={'sm'} shape={'brick'}>
          Все, как договаривались
        </Button>
        <Button variant={'filled'} size={'sm'} shape={'brick'}>
          Заинтересованность
        </Button>
      </div>

      <textarea placeholder={'Расскажите подробнее (макс. 500 символов)'}></textarea>

      <Button variant={'filled'} size={'sm'} shape={'round'}>
        Отправить
      </Button>
    </form>
  );
};

type Props = {
  initialPhone: string;
  onSave: (phone: string) => void;
  onCancel: () => void;
};

export const ChangePhoneModalContent: React.FC<Props> = ({
  initialPhone,
  onSave,
  onCancel,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [newPhone, setNewPhone] = useState(initialPhone);
  const [code, setCode] = useState(['', '', '', '']);
  const [timer, setTimer] = useState(60);

  useEffect(() => {
    if (step === 2 && timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [step, timer]);

  const handleSubmitPhone = (e: React.FormEvent) => {
    e.preventDefault();
    setStep(2);
    setTimer(60);
  };

  const handleSubmitCode = (e: React.FormEvent) => {
    e.preventDefault();
    const enteredCode = code.join('');
    if (enteredCode.length === 4) {
      onSave(newPhone);
    }
  };

  const handleChangeCode = (index: number, value: string) => {
    if (/^\d?$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      if (value && index < 3) {
        const next = document.getElementById(`code-${index + 1}`);
        next?.focus();
      }
    }
  };

  return (
    <form
      className={classes['change-phone-form']}
      onSubmit={step === 1 ? handleSubmitPhone : handleSubmitCode}
    >
      <header>
        <h1>Изменение номера</h1>
        {step === 1 ? <p>Введите новый номер телефона</p> : <p>Введите код из SMS</p>}
      </header>

      {step === 1 && (
        <>
          <TextField
            shape={'brick'}
            value={newPhone}
            onChange={(value) => setNewPhone(value)}
            placeholder='+7 912 876-98-09'
            size={'lg'}
            variant={'outlined'}
            className={classes['change-phone-input']}
          />

          <div className={classes['change-phone-actions']}>
            <Button variant='filled' size='lg' shape='round' color='primary'>
              Отправить код
            </Button>
            <Button
              variant='filled'
              size='lg'
              shape='round'
              color='secondary'
              onClick={onCancel}
            >
              Отмена
            </Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className={classes['code-inputs']}>
            {code.map((digit, i) => (
              <input
                key={i}
                id={`code-${i}`}
                type='text'
                inputMode='numeric'
                maxLength={1}
                value={digit}
                onChange={(e) => handleChangeCode(i, e.target.value)}
                className={classes['code-input']}
              />
            ))}
          </div>

          <div className={classes['change-phone-actions']}>
            <Button variant='filled' size='lg' shape='round' color='primary'>
              Продолжить
            </Button>
          </div>

          <p className={classes['resend-timer']}>
            {timer > 0 ? (
              <>Отправить код повторно через {`0:${timer.toString().padStart(2, '0')}`}</>
            ) : (
              <button
                type='button'
                className={classes['resend-button']}
                onClick={() => {
                  setTimer(60);
                }}
              >
                Отправить код повторно
              </button>
            )}
          </p>
        </>
      )}
    </form>
  );
};
